import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    formatNumber,
    Duration,
    asRows,
    buildMemberRows,
    buildPlatformList,
    buildCountriesList,
    buildCitiesList,
    Section,
    LeaderRow,
    GeoSection,
    SourceRow,
} from "../lib/leaderboard";
import ResultsSkeleton from "../componenets/ResultsSkeleton";
import { readMetricsSnapshot, triggerRefresh } from "../lib/metricsCache";

const Results = () => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            try {
                const snapshot = await readMetricsSnapshot();
                if (cancelled) return;

                if (snapshot && snapshot.payload) {
                    setMetrics(snapshot.payload);
                    setLoading(false);
                    if (snapshot.isStale) {
                        const fresh = await triggerRefresh();
                        if (!cancelled && fresh) setMetrics(fresh);
                    }
                    return;
                }

                const fresh = await triggerRefresh();
                if (cancelled) return;
                if (fresh) setMetrics(fresh);
                else setError({ error: "metrics_unavailable" });
            } catch (err) {
                if (!cancelled) setError({ error: "fetch_failed", detail: err.message });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        run();
        return () => { cancelled = true; };
    }, []);

    const termRows = asRows(metrics && metrics.metrics && metrics.metrics.utm_term);
    const comboRows = asRows(metrics && metrics.metrics && metrics.metrics.combos);
    const countryRows = asRows(metrics && metrics.metrics && metrics.metrics.country);
    const cityRows = asRows(metrics && metrics.metrics && metrics.metrics.city);

    const shareCombos = useMemo(() => comboRows.filter((c) => c.source === "ambasada-share"), [comboRows]);
    const totalShareViews = useMemo(() => shareCombos.reduce((s, c) => s + (c.y || 0), 0), [shareCombos]);
    const platformList = useMemo(() => buildPlatformList(shareCombos, totalShareViews), [shareCombos, totalShareViews]);
    const byTerm = metrics && metrics.byTerm;
    const memberRows = useMemo(() => buildMemberRows(comboRows, termRows, null, byTerm), [comboRows, termRows, byTerm]);

    const namedAll = useMemo(() => memberRows.filter((m) => !m.isAnon), [memberRows]);
    const anonRow = useMemo(() => memberRows.find((m) => m.isAnon) || null, [memberRows]);

    const countriesList = useMemo(() => buildCountriesList(countryRows), [countryRows]);
    const citiesList = useMemo(() => buildCitiesList(cityRows), [cityRows]);

    const maxPlatform = Math.max(1, ...platformList.map((r) => r.value));

    return (
        <div className="bg-brand-bg min-h-screen">
            <header className="bg-white border-b border-brand-panel">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 py-8 sm:py-12 lg:py-14">
                    <h1 className="flex flex-wrap items-center gap-x-4 sm:gap-x-5 gap-y-2 font-brand text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-ink leading-[1.05]">
                        <Link
                            to="/"
                            aria-label="Home"
                            className="inline-flex items-center"
                        >
                            <img
                                src="/ambasada-mark.svg"
                                alt="Ambasada"
                                width="40"
                                height="40"
                                className="h-12 sm:h-14 lg:h-16 w-auto"
                            />
                        </Link>
                        <span>results<span className="text-brand-red">.</span></span>
                    </h1>
                    <p className="mt-4 text-base sm:text-lg text-brand-muted max-w-xl">
                        Every visit below started with someone in this community deciding to share.
                    </p>
                    <div className="mt-5 h-1 w-24 bg-brand-red rounded-full" />
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 sm:px-8 py-10 sm:py-14">
                {loading && <ResultsSkeleton />}

                {error && (
                    <div className="rounded-2xl bg-white border border-brand-red/30 p-6">
                        <div className="text-sm uppercase tracking-wider text-brand-red font-semibold">
                            Metrics unavailable
                        </div>
                        <div className="mt-2 text-brand-ink text-sm">
                            {error.detail || error.error || "Unknown error"}
                        </div>
                    </div>
                )}

                {!loading && !error && metrics && (
                    <>
                        <section className="relative rounded-3xl bg-white border border-brand-panel shadow-brand overflow-hidden">
                            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-brand-red/5 pointer-events-none" aria-hidden="true" />
                            <div className="absolute -bottom-32 -left-20 w-72 h-72 rounded-full bg-brand-red/[0.03] pointer-events-none" aria-hidden="true" />
                            <div className="relative p-8 sm:p-12 lg:p-14">
                                <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-brand-red">
                                    Community share metrics
                                </div>

                                {(() => {
                                    const hasTime = metrics && metrics.shareStats && metrics.shareStats.totaltime > 0;
                                    return (
                                        <div className="mt-8 flex flex-col sm:flex-row sm:items-stretch gap-6 sm:gap-0">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-brand font-bold text-brand-ink text-[72px] sm:text-[88px] lg:text-[112px] leading-[0.9] tracking-tight">
                                                    {formatNumber(totalShareViews)}
                                                </div>
                                                <div className="mt-2 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-brand-faint">
                                                    {totalShareViews === 1 ? "click" : "clicks"} delivered
                                                </div>
                                            </div>
                                            {hasTime && (
                                                <>
                                                    <div className="hidden sm:block w-px self-stretch bg-brand-panel mx-6 lg:mx-10" />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-brand font-bold text-brand-ink text-[72px] sm:text-[88px] lg:text-[112px] leading-[0.9] tracking-tight">
                                                            <Duration seconds={metrics.shareStats.totaltime} />
                                                        </div>
                                                        <div className="mt-2 text-[10px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-brand-faint">
                                                            of prime time
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </section>

                        <Section
                            eyebrow="The leaderboard"
                            title="Top 10"
                            lede="Ranked by prime time their shares generated. Tap a name for the personal page."
                        >
                            {memberRows.length === 0 ? (
                                <div className="rounded-2xl bg-white border border-brand-panel p-6 text-sm text-brand-muted">
                                    No attributed clicks yet. The first share to land here unlocks the leaderboard.
                                </div>
                            ) : (
                                <>
                                    <div className="rounded-2xl bg-white border border-brand-panel shadow-brand overflow-hidden divide-y divide-brand-panel">
                                        {namedAll.slice(0, 10).map((row, i) => (
                                            <LeaderRow key={row.id} row={row} rank={i + 1} />
                                        ))}
                                        {anonRow && <LeaderRow row={anonRow} rank={null} isAnon />}
                                    </div>
                                    {namedAll.length > 10 && (
                                        <div className="mt-4 text-center text-sm text-brand-muted">
                                            + {namedAll.length - 10} more not shown
                                        </div>
                                    )}
                                </>
                            )}
                        </Section>

                        <GeoSection
                            eyebrow="The places"
                            title="Where the clicks land"
                            lede="Countries and cities for share-driven clicks across every track."
                            countries={countriesList}
                            cities={citiesList}
                        />

                        <Section
                            eyebrow="The channels"
                            title="Where they shared"
                            lede="Platforms the community shared on."
                        >
                            <div className="rounded-2xl bg-white border border-brand-panel p-6 sm:p-7 shadow-brand">
                                {platformList.length === 0 ? (
                                    <div className="text-sm text-brand-muted">No platform data yet.</div>
                                ) : (
                                    <div className="space-y-3.5">
                                        {platformList.map((row) => (
                                            <SourceRow key={row.key} row={row} max={maxPlatform} isPlatform />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Section>

                        <section className="mt-16 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-brand-muted">
                            <div>
                                Numbers reflect community share traffic. Refresh to pull latest.
                            </div>
                            <Link to="/" className="font-medium text-brand-ink hover:text-brand-red transition-colors whitespace-nowrap">
                                Back to the feed
                            </Link>
                        </section>
                    </>
                )}
            </main>
        </div>
    );
};

export default Results;
